import type { ReactNode } from "react";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import { Box, CircularProgress, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import dayjs from "dayjs";

import { AttendanceLegend } from "@/containers/staff/staff-attendance/components/AttendanceLegend";
import type {
  AttendanceDayCell,
  AttendanceEmployeeRow,
} from "@/containers/staff/staff-attendance/attendance-types";
import { StaffAttendanceMonthCalendar } from "@/containers/staff/staff/components/StaffAttendanceMonthCalendar";
import { StaffAttendanceSummaryCards } from "@/containers/staff/staff/components/StaffAttendanceSummaryCards";
import { formatIsoMonth } from "@/utils/date-format";

export type StaffMonthlyAttendancePanelProps = {
  month: string;
  onMonthChange: (month: string) => void;
  loading: boolean;
  employeeRow: AttendanceEmployeeRow | null;
  timezone: string;
  offWeekdays: number[];
  lateCheckInLocal: string | null;
  onRefresh: () => void;
  onDayClick: (day: number, cell: AttendanceDayCell) => void;
  /** Extra controls rendered after the month picker (e.g. link buttons). */
  toolbarActions?: ReactNode;
};

export function StaffMonthlyAttendancePanel({
  month,
  onMonthChange,
  loading,
  employeeRow,
  timezone,
  offWeekdays,
  lateCheckInLocal,
  onRefresh,
  onDayClick,
  toolbarActions,
}: StaffMonthlyAttendancePanelProps) {
  const includeLate = Boolean(lateCheckInLocal);

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          label="Month"
          type="month"
          size="small"
          value={month}
          onChange={(e) => {
            onMonthChange(e.target.value || formatIsoMonth(dayjs()));
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: "100%", sm: 180 } }}
        />
        <Tooltip title="Refresh attendance">
          <span>
            <IconButton aria-label="Refresh attendance" onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        {toolbarActions}
      </Stack>

      <Box sx={{ position: "relative" }}>
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
              zIndex: 1,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
        <Stack spacing={2}>
          <StaffAttendanceSummaryCards
            summary={employeeRow?.summary ?? null}
            includeLate={includeLate}
          />
          <AttendanceLegend />
          <StaffAttendanceMonthCalendar
            month={month}
            timezone={timezone}
            offWeekdays={offWeekdays}
            employee={employeeRow}
            onDayClick={onDayClick}
          />
        </Stack>
      </Box>
    </>
  );
}
